<template>
    <div v-show="showing" class="bg-black bg-opacity-60 fixed top-0 left-0 w-full h-full flex flex-col items-center justify-center">
        <div class="w-full flex flex-row justify-center flex-wrap">
            <div v-for="(currency, index) in money" :key="index" class="text-center m-4">
                <!-- Banknotes container -->
                <div class="relative w-[180px] h-[120px] mx-auto">
                    <img
                    v-for="(note, i) in getNotes(currency)"
                    :key="'note-' + i"
                    :src="`${BaseUrl}/img/currencies/${currency.code}/${note}.jpg`"
                    :width="180"
                    class="absolute left-[10px] shadow origin-bottom"
                    :style="{ transform: `rotate(${i * 5}deg)` }"
                    />
                    
                </div>
                <!-- Coins container -->
                <div class="flex justify-center items-center gap-x-1 mt-2 mb-4">
                    <img
                        v-for="(coin, i) in getCoins(currency)"
                        :key="'coin-' + i"
                        :src="`${BaseUrl}/img/currencies/${currency.code}/${getCoinImage(coin)}.png`"
                        :width="40"
                    />
                </div>
                <h3 class="text-lg font-semibold text-white">
                    {{ currency.name }}
                </h3>
            </div>
        </div>
    </div>
</template>
  
<script setup>
import { ref } from 'vue';

const props = defineProps({
    money: {
        type: Array,
        required: true
    }
});

const BaseUrl = ref(baseUrl);

const showing = defineModel('modelValue', {
    default: false,
    type: Boolean,
});
  
// Updated denomination mapping for GBP with fractional coins
const currencyDenominations = {
    GBP: {
        notes: [50, 20, 10, 5],
        coins: [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01]
    },
    USD: {
        notes: [100, 50, 20, 10, 5, 1],
        coins: [0.25, 0.10, 0.05, 0.01]
    }
}

function getCoinImage(coin) {
    return String(coin * 100).padStart(3, '0');
}
  
/**
 * Computes the minimal number of banknotes and coins for a given value.
*/
function getChange(value, denominations) {
    let remaining = value
    const notes = []
    const coins = []
    
    // Calculate banknotes
    for (const note of denominations.notes) {
        const count = Math.floor(remaining / note)
        for (let i = 0; i < count; i++) {
            notes.push(note)
        }
        remaining = +(remaining - note * count).toFixed(2)
    }
    
    // Calculate coins
    for (const coin of denominations.coins) {
        const count = Math.floor(remaining / coin)
        for (let i = 0; i < count; i++) {
            coins.push(coin)
        }
        remaining = +(remaining - coin * count).toFixed(2)
    }
    
    return { notes, coins }
}
  
function getNotes(currency) {
    const { code, value } = currency
    const denominations = currencyDenominations[code] || currencyDenominations.GBP
    return getChange(value, denominations).notes
}
  
function getCoins(currency) {
    const { code, value } = currency
    const denominations = currencyDenominations[code] || currencyDenominations.GBP
    return getChange(value, denominations).coins
}
</script>
  
  <style scoped>
  .wallet {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    flex-wrap: wrap;
  }
  
  .currency {
    text-align: center;
    margin: 1rem;
  }
  
  /* Adjusted container for vertical stacking */
  .banknotes-container {
    position: relative;
    width: 100px;
    height: 150px; /* increased height for vertical display */
    margin: 0 auto;
  }
  
  .note {
    width: 40px;
    height: 80px;
    background-color: #4CAF50;
    color: #fff;
    line-height: 80px;
    text-align: center;
    border: 1px solid #333;
    border-radius: 4px;
    position: absolute;
    left: 10px;
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    transform-origin: bottom center;
  }
  
  /* Coins are stacked in a column */
  .coins-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 10px;
  }
  
  .coin {
    width: 30px;
    height: 30px;
    background-color: #FFD700;
    color: #000;
    line-height: 30px;
    text-align: center;
    border: 1px solid #333;
    border-radius: 50%;
    margin-bottom: 5px;
  }
  </style>
  